// components/BottomNav.jsx — Anamoria SPA
// v1.4 — data-tour on Video button (May 22, 2026)
//
// Changes from v1.3:
//   - Added data-tour="video-btn" to Video button.
//   - Zero logic changes. Zero CSS changes.
//
// v1.3 — data-tour attributes for onboarding tour (May 22, 2026)
//
// Changes from v1.2:
//   - Added data-tour="record-btn" to Record button.
//   - Added data-tour="write-btn" to Write button.
//   - Added data-tour="photo-btn" to Photo button.
//   - Zero logic changes. Zero CSS changes. Attributes are inert strings
//     with zero runtime cost — only read by OnboardingTour.jsx via
//     document.querySelector('[data-tour="..."]').
//   - Standard pattern per Shepherd.js, React Joyride, Intro.js.
//
// v1.2 — Video button added (May 16, 2026)
//
// Changes from v1.1:
//   - Added Video button between Photo and Invite. Navigates to
//     /spaces/:spaceId/video (VideoPage pre-flight screen).
//   - Added VideoIcon import from BrandIcons.jsx v1.1.
//   - activeTab now accepts 'video' in addition to existing values.
//   - All existing buttons (Record, Write, Photo, Invite) byte-identical to v1.1.
//   - No CSS changes — Video button uses the same .btn base class as Write/Photo/Invite.
//
// Props:
//   spaceId   — current space UUID (used for navigation)
//   activeTab — 'record' | 'write' | 'photo' | 'video' | 'invite'

import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecordIcon, WriteIcon, PhotoIcon, VideoIcon, InviteIcon } from './BrandIcons';
import styles from './BottomNav.module.css';

export default function BottomNav({ spaceId, activeTab = 'record' }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // ─── Photo picker handlers ─────────────────────────────────
  function handlePhotoClick() {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  function handlePhotoFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    navigate(`/spaces/${spaceId}/photo`, { state: { file } });
  }

  return (
    <nav className={styles.nav} aria-label="Main navigation">

      {/* Record — primary action */}
      <button
        className={`${styles.btn} ${styles.btnPrimary} ${activeTab === 'record' ? styles.active : ''}`}
        onClick={() => navigate(`/spaces/${spaceId}/record`)}
        title="Record a voice note"
        aria-label="Record a voice note"
        data-tour="record-btn"
      >
        <span className={styles.icon}>
          <RecordIcon />
        </span>
        <span className={styles.label}>Record</span>
      </button>

      {/* Write */}
      <button
        className={`${styles.btn} ${activeTab === 'write' ? styles.active : ''}`}
        onClick={() => navigate(`/spaces/${spaceId}/write`)}
        title="Write a memory"
        aria-label="Write a memory"
        data-tour="write-btn"
      >
        <span className={styles.icon}>
          <WriteIcon />
        </span>
        <span className={styles.label}>Write</span>
      </button>

      {/* Photo — opens file picker in the same user-gesture task (Safari-safe) */}
      <button
        className={`${styles.btn} ${activeTab === 'photo' ? styles.active : ''}`}
        onClick={handlePhotoClick}
        title="Add a photo"
        aria-label="Add a photo"
        data-tour="photo-btn"
      >
        <span className={styles.icon}>
          <PhotoIcon />
        </span>
        <span className={styles.label}>Photo</span>
      </button>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className={styles.fileInput}
        onChange={handlePhotoFileChange}
      />

      {/* Video — navigates to video pre-flight screen */}
      <button
        className={`${styles.btn} ${activeTab === 'video' ? styles.active : ''}`}
        onClick={() => navigate(`/spaces/${spaceId}/video`)}
        title="Record or upload a video"
        aria-label="Record or upload a video"
        data-tour="video-btn"
      >
        <span className={styles.icon}>
          <VideoIcon />
        </span>
        <span className={styles.label}>Video</span>
      </button>

      {/* Invite */}
      <button
        className={`${styles.btn} ${activeTab === 'invite' ? styles.active : ''}`}
        onClick={() => navigate(`/spaces/${spaceId}/invite`)}
        title="Invite someone"
        aria-label="Invite someone"
      >
        <span className={styles.icon}>
          <InviteIcon />
        </span>
        <span className={styles.label}>Invite</span>
      </button>

    </nav>
  );
}
